// ABOUTME: Dialog for editing a single config cell value with level selection.
// ABOUTME: Shows current value, source level, input for new value, and level dropdown.

import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/colors';
import { getLevelLabel, getLevelColor } from './LevelBadge';

export default function ConfigCellEditDialog({
  open,
  onClose,
  onApply,
  onClearOverride,
  product,       // product or category object
  fieldName,
  fieldData,     // { value, level, source }
  shopId,
  entityType,    // 'product' | 'category'
  availableLevels, // which levels can be set from current view
  categories,      // available categories for category dropdown
}) {
  const { t } = useTranslation();
  const [newValue, setNewValue] = useState('');
  const [targetLevel, setTargetLevel] = useState(1);

  useEffect(() => {
    if (open && fieldData) {
      setNewValue(fieldData.value ?? '');
      // Default to the current level if available, otherwise first available
      if (availableLevels && availableLevels.length > 0) {
        const currentLevel = fieldData.level;
        setTargetLevel(
          currentLevel && availableLevels.includes(currentLevel) ? currentLevel : availableLevels[0]
        );
      }
    }
  }, [open, fieldData, availableLevels]);

  const isCategory = fieldName === 'category';
  const isName = fieldName === 'name';
  const isBoolean = fieldName === 'ignore';
  const isTime = fieldName === 'opening_time' || fieldName === 'closing_time';
  const isColor = fieldName === 'color';
  const isText = isName || isTime;

  const handleApply = () => {
    let parsedValue = newValue;
    if (!isBoolean && !isText && !isColor && !isCategory && newValue !== '' && newValue !== null) {
      parsedValue = Number(newValue);
      if (isNaN(parsedValue)) parsedValue = newValue;
    }
    onApply({
      level: targetLevel,
      entityType,
      entityId: entityType === 'product' ? String(product.product_id) : product.id,
      shopId: targetLevel <= 2 ? shopId : null,
      field: fieldName,
      value: parsedValue,
      oldValue: fieldData?.value,
      oldLevel: fieldData?.level,
    });
    onClose();
  };

  const entityLabel = entityType === 'product'
    ? `${product?.name || ''} (#${product?.product_id || ''})`
    : `${product?.name || product?.id || ''}`;

  const getLevelDescription = (level) => {
    const label = getLevelLabel(level, t);
    if (level === 1) return `${label} (${shopId} / #${product?.product_id})`;
    if (level === 2) return `${label} (${shopId} / ${product?.category || product?.id})`;
    if (level === 3) return `${label} (#${product?.product_id})`;
    if (level === 4) return `${label} (${product?.category || product?.id})`;
    return label;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.text.primary }}>
          {t('config.edit.title', { field: t(`config.fields.${fieldName}`) })}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: colors.text.secondary }}>
          {entityType === 'product' ? t('config.fields.product') : t('config.fields.category')}: {entityLabel}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Current value info */}
        <Box sx={{
          backgroundColor: colors.background.light,
          borderRadius: '8px',
          p: 1.5,
          mb: 2,
        }}>
          <Typography sx={{ fontSize: '0.8rem', color: colors.text.secondary }}>
            {t('config.edit.currentValue')}: <strong>{formatDisplayValue(fieldData?.value, fieldName)}</strong>
          </Typography>
          {fieldData?.level && (
            <Typography sx={{ fontSize: '0.75rem', color: colors.text.muted, mt: 0.5 }}>
              {t('config.edit.inheritedFrom')}:{' '}
              <Box component="span" sx={{ color: getLevelColor(fieldData.level), fontWeight: 600 }}>
                {getLevelLabel(fieldData.level, t)}
              </Box>
              {fieldData.source && ` (${fieldData.source})`}
            </Typography>
          )}
        </Box>

        {/* New value input */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, color: colors.text.secondary }}>
            {t('config.edit.newValue')}
          </Typography>
          {isCategory ? (
            <FormControl size="small" fullWidth>
              <Select
                value={newValue || ''}
                onChange={(e) => setNewValue(e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="">
                  <Typography sx={{ fontSize: '0.85rem', color: colors.text.muted }}>-</Typography>
                </MenuItem>
                {(categories || []).map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: cat.color || '#9ca3af',
                      }} />
                      {cat.name || cat.id}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : isColor ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="input"
                type="color"
                value={newValue || '#9ca3af'}
                onChange={(e) => setNewValue(e.target.value)}
                sx={{
                  width: 48,
                  height: 48,
                  border: `1px solid ${colors.border.light}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: '2px',
                  backgroundColor: 'transparent',
                }}
              />
              <TextField
                size="small"
                value={newValue || ''}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="#3B82F6"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
            </Box>
          ) : isBoolean ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={!!newValue}
                onChange={(e) => setNewValue(e.target.checked)}
              />
              <Typography sx={{ fontSize: '0.85rem' }}>
                {newValue ? 'Yes' : 'No'}
              </Typography>
            </Box>
          ) : (
            <TextField
              size="small"
              fullWidth
              type={isText ? 'text' : 'number'}
              value={newValue ?? ''}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder={isTime ? 'HH:MM' : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          )}
        </Box>

        {/* Level selector */}
        <Box>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, color: colors.text.secondary }}>
            {t('config.edit.setAtLevel')}
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={targetLevel}
              onChange={(e) => setTargetLevel(e.target.value)}
              sx={{ borderRadius: '8px' }}
            >
              {(availableLevels || [1, 2, 3, 4, 5]).map(level => (
                <MenuItem key={level} value={level}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: getLevelColor(level),
                    }} />
                    {getLevelDescription(level)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {fieldData?.level && fieldData.level < 5 && (
          <Button
            onClick={() => {
              onClearOverride({
                level: fieldData.level,
                entityType,
                entityId: entityType === 'product' ? String(product.product_id) : product.id,
                shopId: fieldData.level <= 2 ? shopId : null,
                field: fieldName,
              });
              onClose();
            }}
            sx={{ textTransform: 'none', color: colors.status.error, mr: 'auto' }}
          >
            {t('config.edit.clearOverride')}
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('config.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            textTransform: 'none',
            bgcolor: colors.ui.button,
            borderRadius: '8px',
            '&:hover': { bgcolor: colors.ui.buttonHover },
          }}
        >
          {t('config.edit.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function formatDisplayValue(value, fieldName) {
  if (value === null || value === undefined) return '-';
  if (fieldName === 'ignore') return value ? 'Yes' : 'No';
  if (fieldName === 'color') return value || '-';
  return String(value);
}
